import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, Headset } from 'lucide-react';
import { supportGetMyThread, supportSendMessage, supportUnreadCount } from '../services/api';

/**
 * Floating "Message us for help" launcher + chat panel.
 * Users (customer / driver / business) only talk to the JustDeliver admin team — never to each other.
 * Auto-seeds a welcome message on first open and polls every 12s for new admin replies.
 */
const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { day: '2-digit', month: 'short' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export const SupportChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(0);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem('jd_support_fab_dismissed') === '1'; } catch { return false; }
  });
  const listRef = useRef(null);

  // Restore listener for the "Show help" pill
  useEffect(() => {
    const handler = () => setDismissed(false);
    window.addEventListener('jd:restore-help-fabs', handler);
    return () => window.removeEventListener('jd:restore-help-fabs', handler);
  }, []);

  const dismissLauncher = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { sessionStorage.setItem('jd_support_fab_dismissed', '1'); } catch {}
    setDismissed(true);
    window.dispatchEvent(new Event('jd:fab-visibility-changed'));
  };

  const scrollToBottom = () => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  };

  const loadThread = useCallback(async () => {
    try {
      setLoading(true);
      const res = await supportGetMyThread();
      setMessages(res.data.messages || []);
      setUnread(0); // opening the panel marks admin msgs as read server-side
    } catch {
      /* silent — chat is a nice-to-have, never block the page */
    } finally {
      setLoading(false);
      setTimeout(scrollToBottom, 50);
    }
  }, []);

  // Poll unread when closed; poll full thread when open.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        if (open) {
          const res = await supportGetMyThread();
          if (!cancelled) {
            setMessages((prev) => {
              const next = res.data.messages || [];
              if (next.length !== prev.length) setTimeout(scrollToBottom, 50);
              return next;
            });
          }
        } else {
          const res = await supportUnreadCount();
          if (!cancelled) setUnread(res.data.unread || 0);
        }
      } catch {
        /* ignore polling errors */
      }
    };
    tick();
    const id = setInterval(tick, open ? 8000 : 20000);
    return () => { cancelled = true; clearInterval(id); };
  }, [open]);

  const handleOpen = async () => {
    setOpen(true);
    await loadThread();
  };

  const handleSend = async (e) => {
    e?.preventDefault?.();
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    // Optimistic — show locally so it feels instant.
    const localId = `tmp-${Date.now()}`;
    const optimistic = {
      id: localId,
      body,
      from_admin: false,
      created_at: new Date().toISOString(),
      pending: true,
    };
    setMessages((m) => [...m, optimistic]);
    setDraft('');
    setTimeout(scrollToBottom, 30);
    try {
      const res = await supportSendMessage(body);
      setMessages((m) => m.map((x) => (x.id === localId ? res.data : x)));
    } catch (err) {
      setMessages((m) => m.filter((x) => x.id !== localId));
      setDraft(body);
      alert(err?.response?.data?.detail || 'Could not send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // Don't render anything for unauthenticated routes.
  return (
    <>
      {/* Launcher — sits ABOVE the WhatsApp FAB (which is at bottom-20 right-6, 56px tall) on every viewport. */}
      {!open && !dismissed && (
        <div className="fixed z-40 bottom-40 right-4 md:right-6" data-testid="support-chat-launcher-wrap">
          <button
            type="button"
            onClick={handleOpen}
            className="inline-flex items-center gap-2 pl-3 pr-4 py-3 rounded-full bg-[#2E7D32] text-white shadow-lg hover:bg-[#1B5E20] hover:shadow-xl active:scale-95 transition-all font-semibold"
            aria-label="Message us for help"
            data-testid="support-chat-launcher"
          >
            <span className="relative">
              <MessageCircle className="w-5 h-5" strokeWidth={2} />
              {unread > 0 && (
                <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white" data-testid="support-unread-badge">
                  {unread > 9 ? '9+' : unread}
                </span>
              )}
            </span>
            <span className="hidden sm:inline">Message us for help</span>
            <span className="sm:hidden">Help</span>
          </button>
          <button
            type="button"
            onClick={dismissLauncher}
            aria-label="Hide help button"
            data-testid="support-chat-launcher-close"
            className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-white text-gray-700 border border-gray-300 shadow-sm flex items-center justify-center hover:bg-gray-100 hover:text-gray-900"
          >
            <X className="w-3 h-3" strokeWidth={2.5} />
          </button>
        </div>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed z-50 bottom-40 right-4 md:right-6 w-[calc(100vw-2rem)] sm:w-[380px] h-[65vh] max-h-[560px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
          data-testid="support-chat-panel"
          role="dialog"
          aria-label="Support chat"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-[#2E7D32] text-white">
            <div className="flex items-center gap-2">
              <span className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
                <Headset className="w-5 h-5" />
              </span>
              <div>
                <p className="font-bold text-sm leading-tight">JustDeliver Support</p>
                <p className="text-[11px] text-white/85 leading-tight">We reply ASAP · Mon-Sun</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="w-8 h-8 rounded-full hover:bg-white/15 inline-flex items-center justify-center transition-colors"
              aria-label="Close chat"
              data-testid="support-chat-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={listRef}
            className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-gradient-to-b from-gray-50 to-white"
            data-testid="support-chat-messages"
          >
            {loading && messages.length === 0 && (
              <p className="text-center text-xs text-gray-400 py-6">Loading…</p>
            )}
            {messages.map((m) => (
              <div
                key={m.id}
                className={`flex ${m.from_admin ? 'justify-start' : 'justify-end'}`}
                data-testid={`support-msg-${m.id}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm leading-snug whitespace-pre-wrap break-words ${
                    m.from_admin
                      ? 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm'
                      : `bg-[#2E7D32] text-white rounded-br-sm ${m.pending ? 'opacity-60' : ''}`
                  }`}
                >
                  {m.body}
                  <div className={`mt-1 text-[10px] ${m.from_admin ? 'text-gray-400' : 'text-white/70'}`}>
                    {m.from_admin && <span className="font-semibold mr-1">Support</span>}
                    {fmtTime(m.created_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Composer */}
          <form
            onSubmit={handleSend}
            className="border-t border-gray-200 p-2.5 bg-white flex items-end gap-2"
          >
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend(e);
                }
              }}
              placeholder="Type a message…"
              rows={1}
              className="flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E7D32]/30 max-h-32"
              data-testid="support-chat-input"
              disabled={sending}
            />
            <button
              type="submit"
              disabled={!draft.trim() || sending}
              className="h-10 w-10 rounded-full bg-[#2E7D32] text-white flex items-center justify-center disabled:opacity-40 hover:bg-[#1B5E20] active:scale-95 transition-all flex-shrink-0"
              data-testid="support-chat-send"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default SupportChat;
